import Button from "@edaz/edazkit/Button/index";
import AdvancedSelect from "@edaz/edazkit/controls/AdvancedSelect";
import { Check, Input } from "@frui.ts/bootstrap";
import { preventDefault } from "@frui.ts/views";
import AccountNumberOrIbanAndBankCodeInput from "controls/finance/accountNumberOrIbanAndBankCodeInput";
import omit from "lodash/omit";
import { observer } from "mobx-react-lite";
import React from "react";
import { ButtonToolbar } from "react-bootstrap";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { isFilterDirty, isFilterEmpty } from "utils/filters";
import { translateGeneral } from "utils/localizations";
import { onNumberInputKeyPress } from "utils/numberInput";
import type InternalTransfersViewModel from "viewModels/finance/internalTransfers/internalTransfersViewModel";

export interface InternalTransfersFiltersProps {
  vm: InternalTransfersViewModel;
}

function omitIsExported(entity: Record<string, any>) {
  return omit(entity, ["isExported"]);
}

const InternalTransfersFilters = observer((props: InternalTransfersFiltersProps) => {
  const { vm } = props;
  const fullTextClassNames = vm.filter.fullTextSearchEnabled ? "border-success border-2" : "";

  const searchDisabled =
    (isFilterEmpty(omitIsExported(vm.filter)) && vm.filter.isExported === false) || !isFilterDirty(vm.filter);

  const canResetFilter = isFilterDirty(vm.filter) || !isFilterEmpty(omitIsExported(vm.filter)) || vm.filter.isExported !== false;

  return (
    <Form className="mb-4" onSubmit={preventDefault(vm.applyFilterAndLoad)} onReset={preventDefault(vm.resetFilterAndLoad)}>
      <div className="row">
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "senderBankAccount")}</Form.Label>
          <AccountNumberOrIbanAndBankCodeInput
            target={vm.filter}
            property="senderBankAccount"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "recipientBankAccount")}</Form.Label>
          <AccountNumberOrIbanAndBankCodeInput
            target={vm.filter}
            property="recipientBankAccount"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "messageForRecipient")}</Form.Label>
          <Input
            target={vm.filter}
            property="messageForRecipient"
            placeholder={translateGeneral("search")}
            className={fullTextClassNames}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "amount")}</Form.Label>
          <Input target={vm.filter} property="amount" step={0.01} placeholder={translateGeneral("search")} type="number" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "variableSymbol")}</Form.Label>
          <Input
            target={vm.filter}
            property="variableSymbol"
            placeholder={translateGeneral("search")}
            type="number"
            onKeyDown={onNumberInputKeyPress}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "constantSymbol")}</Form.Label>
          <Input target={vm.filter} property="constantSymbol" placeholder={translateGeneral("search")} type="number" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "specificSymbol")}</Form.Label>
          <Input target={vm.filter} property="specificSymbol" placeholder={translateGeneral("search")} type="number" />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "_approvalState")}</Form.Label>
          <AdvancedSelect
            target={vm}
            property="_approvalState"
            items={vm.approvalsOptions}
            allowEmpty={true}
            placeholder={translateGeneral("select")}
          />
        </Form.Group>
        <Form.Group as={Col} md={6} lg={4} xl={3}>
          <Form.Label>{vm.translateFilterAttribute("internalTransfersRequest", "isExported")}</Form.Label>
          <AdvancedSelect
            placeholder={translateGeneral("select")}
            target={vm.filter}
            property="isExported"
            allowEmpty={true}
            items={[
              { id: true, label: vm.translateIsExported("exported") },
              { id: false, label: vm.translateIsExported("not-exported") },
            ]}
          />
        </Form.Group>
      </div>
      <div className="row mt-2">
        <div className="col-lg-auto order-lg-2">
          <Check
            className="mt-lg-3 mb-3 mb-lg-0"
            target={vm.filter}
            property="fullTextSearchEnabled"
            disabled={isFilterEmpty(vm.filter)}
            custom
            label={
              <>
                <span>{vm.translate("allowFullTextSearch")}</span>
                {vm.filter.fullTextSearchEnabled && (
                  <span className="text-muted font-size-sm pl-4">{vm.translate("noticeFullTextSearch")}</span>
                )}
              </>
            }
          />
        </div>
        <div className="col-lg-auto">
          <ButtonToolbar className="mt-2 align-items-center">
            <Button size="sm" type="submit" disabled={searchDisabled}>
              {translateGeneral("search")}
            </Button>
            {canResetFilter && (
              <Button size="sm" type="reset" variant="outline-danger">
                {translateGeneral("resetFilter")}
              </Button>
            )}
          </ButtonToolbar>
        </div>
      </div>
    </Form>
  );
});

export default InternalTransfersFilters;
